import type { MarketEstimate } from "@hobbyshopify/backend-client";
import { formatCents } from "../core/primitives/card-display";
import {
  type MarketRefreshStatus,
  marketFailureText,
  marketFreshness,
  marketFreshnessText,
  refreshOutcomeText,
} from "../core/primitives/catalog-market";

const FRESHNESS_TONE = { unavailable: "text-faint", stale: "text-warn", fresh: "text-muted" } as const;

/**
 * The shared market estimate of a product, beside the shop's own price but
 * never the same number: refreshing it changes nothing the shop asks.
 */
export function MarketEstimatePanel({
  estimate,
  refresh,
  onRefresh,
  onUse,
}: {
  estimate: MarketEstimate | null | undefined;
  refresh: MarketRefreshStatus;
  /** Absent when the product has no estimate to refresh. */
  onRefresh?: () => void;
  /** Copies the market price into the asking price field. */
  onUse?: (cents: number) => void;
}) {
  const freshness = marketFreshness(estimate);
  const failure = marketFailureText(estimate);
  const busy = refresh.kind === "busy";
  const market = estimate?.marketCents ?? null;

  return (
    <div className="rounded-row border border-line bg-surface p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs font-medium text-muted">Market price</div>
          <div className="text-lg font-semibold text-white">{market != null ? formatCents(market) : "—"}</div>
          {(estimate?.dealerBuyCents != null || estimate?.dealerSellCents != null) && (
            <div className="text-xs text-faint">
              Dealer buy {formatCents(estimate?.dealerBuyCents)} · sell {formatCents(estimate?.dealerSellCents)}
            </div>
          )}
        </div>
        <div className="flex shrink-0 gap-1">
          {onUse && market != null && (
            <button type="button" className="btn-ghost btn-xs" onClick={() => onUse(market)}>
              Use as asking price
            </button>
          )}
          {onRefresh && estimate && (
            <button type="button" className="btn-secondary btn-xs" disabled={busy} onClick={onRefresh}>
              {busy ? "Refreshing…" : "Refresh"}
            </button>
          )}
        </div>
      </div>
      <p className={`mt-2 text-xs ${FRESHNESS_TONE[freshness]}`}>{marketFreshnessText(estimate)}</p>
      {failure && <p className="mt-1 text-xs text-warn">{failure}</p>}
      {refresh.kind === "done" && <p className="mt-1 text-xs text-muted">{refreshOutcomeText(refresh.outcome)}</p>}
      {refresh.kind === "error" && <p className="mt-1 text-xs text-danger">{refresh.message}</p>}
    </div>
  );
}
